import * as React from 'react';
import { clsx } from 'clsx';
import { motion } from 'framer-motion';

type CardVariant = 'default' | 'elegant';
type CardPadding = 'sm' | 'md' | 'lg' | 'xl';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
  padding?: CardPadding;
  variant?: CardVariant;
  hover?: boolean;
}

export const Card: React.FC<CardProps> = ({ 
  children, 
  className,
  padding = 'md',
  variant = 'default',
  hover = false,
  ...props
}) => {
  // Separate drag and animation related props to avoid conflicts
  const { 
    onDrag, onDragEnd, onDragStart, onDragLeave, onDragEnter, onDragOver, onDrop,
    onAnimationStart, onAnimationEnd, onAnimationIteration,
    ...restProps 
  } = props as any;
  const paddingClasses: Record<CardPadding, string> = {
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
    xl: 'p-10',
  };

  const variantClasses: Record<CardVariant, string> = {
    default: 'bg-neutral-100/50 border border-neutral-200/80 shadow-sm',
    elegant: 'bg-white border border-neutral-200 shadow-md',
  };

  const hoverClasses = hover ? 'hover:shadow-lg hover:-translate-y-1 transition-transform duration-300 cursor-pointer' : '';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-50px" }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      whileHover={hover ? { 
        scale: 1.02, 
        y: -4,
        transition: { type: "spring", stiffness: 300, damping: 20 }
      } : {}}
      whileTap={hover ? { scale: 0.98 } : {}}
      className={clsx(
        'rounded-2xl transition-all duration-300',
        variantClasses[variant],
        paddingClasses[padding],
        hoverClasses,
        className
      )}
      {...restProps}
    >
      {children}
    </motion.div>
  );
};

// CardHeader component
export const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={clsx("flex flex-col space-y-1.5 px-6 py-6", className)}
    {...props}
  />
));
CardHeader.displayName = "CardHeader";

// CardTitle component
export const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={clsx(
      "text-2xl font-semibold leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
CardTitle.displayName = "CardTitle";

// CardDescription component
export const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={clsx("text-sm text-muted-foreground", className)}
    {...props}
  />
));
CardDescription.displayName = "CardDescription";

// CardContent component
export const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={clsx("px-6 pb-6", className)} {...props} />
));
CardContent.displayName = "CardContent";

// CardFooter component
export const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={clsx("flex items-center px-6 py-6 pt-0", className)}
    {...props}
  />
));
CardFooter.displayName = "CardFooter";